import React, { useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import { useGetCurrentUserQuery, useUpdateCurrentUserMutation } from "../../../app/accounts/authuser";

import Input from "../../../components/form-item/Input";
import Select from "../../../components/form-item/Select";
import TitleBlock from "../../../components/block/TitleBlock";
import SvgLoadingAnimation from "../../../components/loading/index";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";

const BankAccount = () => {
  // RTK Queryフックを使用
  const { data: userData, isLoading, error } = useGetCurrentUserQuery();
  const [updateCurrentUser] = useUpdateCurrentUserMutation();

  // ローカル状態管理
  const [formData, setFormData] = useState({});
  const [updateStatus, setUpdateStatus] = useState("idle");
  const [errors, setErrors] = useState({});
  const debounceTimeoutRef = React.useRef(null);
  const isComposingRef = React.useRef(false); // IME変換状態管理

  // ユーザーデータの初期化
  useEffect(() => {
    if (userData?.data) {
      setFormData({
        bank: userData.data.bank || "",
        bank_branch: userData.data.bank_branch || "",
        bank_branch_number: userData.data.bank_branch_number || "",
        account_type: userData.data.account_type || "normal",
        account_number: userData.data.account_number || "",
        account_holder: userData.data.account_holder || "",
      });
    }
  }, [userData]);

  // デバウンス付きリアルタイム更新機能
  const debouncedUpdate = useCallback(async (fieldName, fieldValue) => {
    try {
      setUpdateStatus("saving");
      await updateCurrentUser({ [fieldName]: fieldValue }).unwrap();
      setUpdateStatus("saved");
      // 1秒後にsavedステータスをクリア
      setTimeout(() => setUpdateStatus("idle"), 1000);
    } catch (error) {
      console.error("Failed to update bank account:", error);
      setUpdateStatus("error");
      setTimeout(() => setUpdateStatus("idle"), 3000);
    }
  }, [updateCurrentUser]);

  // 単一フィールドのバリデーション
  const validateField = (name, value) => {
    switch (name) {
      case "bank_branch_number":
        if (value && !/^\d{3}$/.test(value)) {
          return "3桁の数字で入力してください。";
        }
        break;
      case "account_number":
        if (value && !/^\d{7}$|^\d{10}$/.test(value)) {
          return "7桁または10桁の数字で入力してください。";
        }
        break;
      default:
        break;
    }
    return null;
  };

  // 入力変更時の処理
  const handleChange = (eOrName, valueArg) => {
    let name, value;
    if (typeof eOrName === "string") {
      // compositionendから直接呼ばれた場合
      name = eOrName;
      value = valueArg;
    } else {
      // 通常のonChangeイベント
      name = eOrName.target.name;
      value = eOrName.target.value;
    }

    if (isComposingRef.current) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      return;
    }

    // 語尾の正規化
    if (name === "bank" && value.endsWith("銀行")) {
      value = value.slice(0, -2);
    }
    if (name === "bank_branch" && value.endsWith("支店")) {
      value = value.slice(0, -2);
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // 単一フィールドのバリデーション実行
    const fieldError = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: fieldError
    }));

    // 既存のタイマーをクリア
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // 500ms後に更新実行
    debounceTimeoutRef.current = setTimeout(() => {
      // バリデーションエラーがない場合のみ更新
      if (!fieldError) {
        debouncedUpdate(name, value);
      }
    }, 500);
  };

  // compositionイベントハンドラ
  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };
  const handleCompositionEnd = (e) => {
    // 即座にisComposingをfalseにする
    isComposingRef.current = false;
    // 手動でhandleChangeを呼び出す（onChangeが発火しない場合の対策）
    handleChange(e.target.name, e.target.value);
  };

  // クリーンアップ処理
  React.useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  if (isLoading) return <SvgLoadingAnimation />;
  if (error) return <div>Error: {error.message || 'データの取得に失敗しました'}</div>;

  return (
    <>
      <TitleBlock
        title="振込先銀行口座"
        description="残高を出金するための銀行口座を登録"
      />
      <div className="u-col u-gap24 u-w100">
        <Input
          label="銀行名"
          option="optional"
          type="text"
          placeholder="楽天"
          width="100%"
          append="銀行"
          name="bank"
          value={formData.bank || ""}
          onChange={handleChange}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          error={errors.bank}
        />
        <div className="u-row u-gap16 u-w100">
          <Input
            label="支店"
            option="optional"
            type="text"
            placeholder="青海"
            append="支店"
            name="bank_branch"
            value={formData.bank_branch || ""}
            onChange={handleChange}
            error={errors.bank_branch}
          />
          <Input
            label="支店番号"
            option="optional"
            type="text"
            placeholder="123"
            name="bank_branch_number"
            maxLength="3"
            inputMode="numeric"
            value={formData.bank_branch_number || ""}
            onChange={handleChange}
            error={errors.bank_branch_number}
          />
        </div>
        <div className="u-row u-gap16 u-w100">
          <Select
            value={formData.account_type || "normal"}
            label="口座種別"
            options={[
              { value: "normal", name: "普通" },
              { value: "temp", name: "当座" },
              { value: "reverse_tax", name: "納税準備預金" },
              { value: "saving", name: "貯蓄" },
              { value: "other", name: "その他" },
            ]}
            name="account_type"
            onChange={(e) =>
              handleChange({
                target: { name: "account_type", value: e.target.value },
              })
            }
          />
          <Input
            label="口座番号"
            option="optional"
            type="text"
            placeholder="1234567"
            name="account_number"
            inputMode="numeric"
            maxLength="10"
            value={formData.account_number || ""}
            onChange={handleChange}
            error={errors.account_number}
          />
        </div>
        <Input
          label="口座名義（カナ）"
          option="optional"
          type="text"
          placeholder="カ）ソレモ"
          name="account_holder"
          value={formData.account_holder || ""}
          onChange={handleChange}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
        />
        <StatusIndicator status={updateStatus} />
      </div>
    </>
  );
};

export default BankAccount;
